import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { CustomerCarrier } from "~/shared/schemas/auto-dispatch-schema.ts";
import {
	disableCustomerCarrierRequestSchema,
	disabledCustomerCarrierSchema,
} from "~/shared/schemas/auto-dispatch-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";

import { customerEnabledCarriersQueryKey } from "./use-get-customer-carriers.ts";

const updateCustomerCarrier = async (
	updatedCarrier: CustomerCarrier,
	signal?: AbortSignal,
) => {
	const signalOptions = signal ? { signal } : undefined;

	if (updatedCarrier.canAutoDispatch) {
		const url = apiRoutes.ENABLECUSTOMERCARRIER.replace(
			":customerId",
			updatedCarrier.customerId.toString(),
		).replace(":carrierId", updatedCarrier.carrierId.toString());
		const resp = await deleteAndValidate(url, z.undefined(), signalOptions);
		if (resp.success || resp.status === 404) {
			return;
		}
		throw new Error(
			`Failed to enable carrier ${updatedCarrier.carrierName}: ${resp.error}`,
		);
	}

	const parsedJson = disableCustomerCarrierRequestSchema.safeParse({
		customerID: updatedCarrier.customerId,
		carrierID: updatedCarrier.carrierId,
	});
	if (!parsedJson.success) {
		throw new Error(`Invalid carrier data: ${parsedJson.error.message}`);
	}

	const resp = await postAndValidate(
		apiRoutes.DISABLECUSTOMERCARRIER,
		parsedJson.data,
		disabledCustomerCarrierSchema,
		signalOptions,
	);
	if (resp.success || resp.status === 409) {
		return;
	}
	throw new Error(
		`Failed to disable carrier ${updatedCarrier.carrierName}: ${resp.error}`,
	);
};

export const useUpdateCustomerCarriers = (customerId: number) => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const { mutateAsync: patchCarrier } = useMutation({
		mutationFn: (updatedCarrier: CustomerCarrier) =>
			updateCustomerCarrier(updatedCarrier),

		onMutate: async (updatedCarrier) => {
			await queryClient.cancelQueries({
				queryKey: customerEnabledCarriersQueryKey(customerId),
			});
			const queryKey = customerEnabledCarriersQueryKey(customerId);
			const previous = queryClient.getQueryData<CustomerCarrier[]>(queryKey);

			queryClient.setQueryData(queryKey, (old: CustomerCarrier[] = []) =>
				old.map((carrier) =>
					carrier.carrierId === updatedCarrier.carrierId
						? updatedCarrier
						: carrier,
				),
			);

			return { previous };
		},

		onSuccess: () => {
			showSuccess("Carrier Selection Updated");
		},

		onError: (_err, _update, context) => {
			showError(`${_err}`);
			queryClient.setQueryData(
				customerEnabledCarriersQueryKey(customerId),
				context?.previous,
			);
		},

		onSettled: () =>
			queryClient.invalidateQueries({
				queryKey: customerEnabledCarriersQueryKey(customerId),
			}),
	});

	return { patchCarrier };
};

export const useUpdateCustomerCarrier = (customerId: number) => {
	const { patchCarrier } = useUpdateCustomerCarriers(customerId);
	return patchCarrier;
};
